import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useFixedAssetValues } from '@/hooks/useFixedAssetValues';

const valueRecordingSchema = z.object({
  value: z
    .number({ invalid_type_error: 'Value must be a number' })
    .positive('Value must be greater than 0'),
  recorded_at: z.string().min(1, 'Date is required'),
  notes: z.string().optional(),
});

export type ValueRecordingFormData = z.infer<typeof valueRecordingSchema>;

interface ValueRecordingFormProps {
  assetId: string;
  onSuccess?: () => void;
}

export function ValueRecordingForm({ assetId, onSuccess }: ValueRecordingFormProps) {
  const { createValue } = useFixedAssetValues(assetId);

  const form = useForm<ValueRecordingFormData>({
    resolver: zodResolver(valueRecordingSchema),
    defaultValues: {
      value: undefined as unknown as number,
      recorded_at: new Date().toISOString().split('T')[0],
      notes: '',
    },
  });

  const { isSubmitting } = form.formState;

  const onSubmit = async (data: ValueRecordingFormData) => {
    try {
      await createValue.mutateAsync({
        fixed_asset_id: assetId,
        value: data.value,
        recorded_at: data.recorded_at,
        notes: data.notes || undefined,
      });
      toast.success('Value recorded successfully');
      form.reset({
        value: undefined as unknown as number,
        recorded_at: new Date().toISOString().split('T')[0],
        notes: '',
      });
      onSuccess?.();
    } catch {
      toast.error('Failed to record value. Please try again.');
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="value"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Value *</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? undefined : parseFloat(e.target.value))
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="recorded_at"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Date *</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes (optional)</FormLabel>
              <FormControl>
                <Textarea placeholder="Add any notes..." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          <Button type="submit" disabled={isSubmitting || createValue.isPending}>
            {isSubmitting || createValue.isPending ? 'Recording...' : 'Record Value'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
